import { useState } from "react";
import { ConfirmDialog, Field, Textarea } from "../../../../components/ui";
import { formatDay } from "../myHrText";
import { roleName } from "../employeeView";

const isRider = (member) => String(member?.role || "").toUpperCase() === "RIDER";
const sinceDay = (value) => (value ? formatDay(String(value).slice(0, 10), { year: true }) : null);

/**
 * Suspend (C3, revokes access). What the server does: the membership becomes suspended, so the business portal and
 * Rider delivery work no longer include them. They stay in payroll: pay already earned is kept, and suspended days are
 * unpaid until they are reinstated. A Rider still holding parcels or cash is refused by the server. Records are untouched.
 */
export function SuspendDialog({ member, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  return (
    <ConfirmDialog
      title={`Suspend ${member.name}?`}
      facts={[
        { label: "Role", value: roleName(member) || "Not set" },
        { label: "Department", value: member.department || "Not set" },
      ]}
      consequence={`Until they are reinstated they cannot sign in to the business${isRider(member) ? " or take delivery work" : ""}. Pay they have already earned is kept; from today, suspended days are unpaid until they are reinstated. Their records and pay settings stay as they are.`}
      confirmLabel="Suspend employee"
      cancelLabel="Keep active"
      tone="danger"
      onConfirm={() => onConfirm(reason.trim())}
      onClose={onClose}
    >
      <Field label="Reason" hint="Optional. Kept in their employment history.">
        <Textarea rows={2} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} />
      </Field>
    </ConfirmDialog>
  );
}

/** Reinstate (C3, restores access). Someone suspended during onboarding returns to onboarding, not to work. */
export function ReinstateDialog({ member, returnsToOnboarding, onConfirm, onClose }) {
  const facts = [{ label: "Role", value: roleName(member) || "Not set" }];
  if (member.suspendedAt) facts.push({ label: "Suspended since", value: sinceDay(member.suspendedAt) });
  if (member.suspensionReason) facts.push({ label: "Reason", value: member.suspensionReason });
  return (
    <ConfirmDialog
      title={`Reinstate ${member.name}?`}
      facts={facts}
      consequence={returnsToOnboarding
        ? "They return to onboarding, where they were when suspended. They start work once onboarding is finished."
        : `They can sign in${isRider(member) ? " and take delivery work" : ""} again, and their pay resumes from today. The days they were suspended stay unpaid.`}
      confirmLabel="Reinstate employee"
      cancelLabel="Keep suspended"
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}
